import type { ReactNode } from 'react';
import React, { createContext, useCallback, useState } from 'react';
import { useQuery, ApolloError } from '@apollo/client';

import { GET_ALL_PROJECTS } from '../graphql/GET_ALL_PROJECTS';
import { renderContents } from './common';

export const PAGE_LIMIT = 5;

interface initialStateType {
  error: ApolloError | undefined,
  isLoading: boolean,
  onFetchMore: any,
  page: number,
  projects: any[],
  totalCount: number
}

const initialState: initialStateType = {
  error: undefined,
  isLoading: false,
  onFetchMore: undefined,
  page: 1,
  projects: [],
  totalCount: 0
};

export const ProjectListContext = createContext(initialState);

interface IProjectContextProviderProps {
  children: ReactNode;
}

export const ProjectListContextProvider = (
  props: IProjectContextProviderProps
) => {
  const { loading, error, data, fetchMore } = useQuery(GET_ALL_PROJECTS, {
    variables: { limit: PAGE_LIMIT, page: 1 }
  });
  const [pageNum, setPageNum] = useState<number>(2);
  const [isExhausted, setIsExhausted] = useState(false);

  const projects =
    data && data.getAllProjects ? data.getAllProjects.projects : [];
  const serverTotalCount =
    data && data.getAllProjects ? data.getAllProjects.totalCount : 0;
  const totalCount = isExhausted ? projects.length : serverTotalCount;

  const onFetchMore = useCallback(async () => {
    if (isExhausted || projects.length >= totalCount) {
      return;
    }

    const result = await fetchMore({
      updateQuery: (cache, { fetchMoreResult }) => {
        if (!fetchMoreResult?.getAllProjects) {
          return cache;
        }

        const incoming = fetchMoreResult.getAllProjects.projects || [];
        const existing = cache.getAllProjects.projects || [];
        const seen = new Set(existing.map((project: { id: string }) => project.id));
        const uniqueIncoming = incoming.filter(
          (project: { id: string }) => !seen.has(project.id)
        );
        const merged = [...existing, ...uniqueIncoming];
        const reachedEnd = incoming.length === 0 || incoming.length < PAGE_LIMIT;

        if (reachedEnd) {
          setIsExhausted(true);
        }

        return {
          ...cache,
          getAllProjects: {
            ...cache.getAllProjects,
            ...fetchMoreResult.getAllProjects,
            projects: merged,
            totalCount: reachedEnd
              ? merged.length
              : fetchMoreResult.getAllProjects.totalCount ??
                cache.getAllProjects.totalCount
          }
        };
      },
      variables: {
        limit: PAGE_LIMIT,
        page: pageNum
      }
    });

    const incoming = result?.data?.getAllProjects?.projects || [];
    if (incoming.length === 0 || incoming.length < PAGE_LIMIT) {
      setIsExhausted(true);
    } else {
      setPageNum((prevNum) => prevNum + 1);
    }
  }, [fetchMore, isExhausted, pageNum, projects.length, totalCount]);

  return (
    <ProjectListContext.Provider
      value={{
        error,
        onFetchMore,
        isLoading: loading,
        projects,
        page: data && data.getAllProjects ? data.getAllProjects.page : 1,
        totalCount
      }}
    >
      {renderContents({
        children: props.children,
        data,
        error,
        loading
      })}
    </ProjectListContext.Provider>
  );
};
